import { Component, DestroyRef, inject } from '@angular/core';
import { AuthService } from '../../../../shared/libs/auth/auth.service';
import { Router } from '@angular/router';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { SharedQueries } from 'src/app/shared/types/get-query';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter, map, startWith } from 'rxjs';
import { PageInfo } from 'src/app/shared/types/page-info.type';
import { FormControl } from '@angular/forms';

type Company = {
	id: number;
	name: string;
};

@Component({
	standalone: false,
	selector: 'bt-company-selection',
	templateUrl: './company-selection.component.html',
	styleUrls: ['./company-selection.component.scss']
})
export class CompanySelectionComponent {
	readonly #graphQLService = inject(GraphQLService);
	readonly #destroyRef = inject(DestroyRef);
	readonly #router = inject(Router);
	readonly #authService = inject(AuthService);

	companyNameControl = new FormControl<Company | string>(null);
	companies: Array<Company> = [];
	pageInfo: PageInfo = { hasPreviusPage: false, hasNextPage: false, startCursor: null, endCursor: null };
	totalElements: number = 0;

	constructor() {
		this.companyNameControl.valueChanges
			.pipe(
				takeUntilDestroyed(),
				startWith(''),
				debounceTime(300),
				filter((value) => typeof value === 'string')
			)
			.subscribe((value: string) => {
				this.getCompanies(value, true);
			});
	}

	displayCompany(company: Company): string {
		return company?.name || '';
	}

	inViewportAction(ev: { isVisible: boolean }, item: Company) {
		if (!ev.isVisible) return;
		const index = this.companies.findIndex((el) => el === item);
		if (index === this.companies.length - 1 && this.pageInfo.hasNextPage) {
			this.getCompanies(this.companyNameControl.value as string, false);
		}
	}

	companySelection(): void {
		const companyId = (this.companyNameControl.value as Company).id;
		this.#authService
			.authentication(this.#router.routerState.snapshot, companyId)
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe((res: any) => {
				//AGGIUNGERE UN COOKIE COMPANY-SELECTED
				document.cookie = `COMPANY_SELECTED=${companyId}; path=/;`;

				if (this.#authService.isCompany()) {
					this.#router.navigate(['/company']);
				} else if (this.#authService.isPhotographer()) {
					this.#router.navigate(['/photographer']);
				}
			});
	}

	private getCompanies(search: string, isFirst?: boolean): void {
		const variables = { search: search, first: 10, after: isFirst ? null : this.pageInfo.endCursor };
		this.#graphQLService
			.getData(SharedQueries.GET_COMPANIES, variables)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['companies'])
			)
			.subscribe((res) => {
				this.companies = isFirst ? res.nodes : [...this.companies, ...res.nodes];
				this.pageInfo = res.pageInfo;
				this.totalElements = res.totalCount;
			});
	}
}
